import * as THREE from "three";
import type { World as SceneWorld } from "./scene";
import type { FocusHandle } from "./focus";
import type { CatHandle } from "./cat";

// Player-controls + idle-camera wiring for the cat. WASD + Shift run,
// right-click → walk-to waypoint, and a slow camera-target drift after
// a few idle seconds so the static scene doesn't feel frozen. Lifted
// out of main.ts so adding a new input gesture is a one-file edit.

export interface CatControlsDeps {
  canvas: HTMLCanvasElement;
  world: SceneWorld;
  focus: FocusHandle;
  /** Read live so a late-arriving cat picks up subsequent input
   * without re-wiring on cat load. */
  getCat: () => CatHandle | null;
  /** WASD only steers the cat while debug mode is on — otherwise the
   * keys are reserved for normal text input (typing into search) and
   * the casual user never accidentally yanks the cat across the scene
   * by pressing 'a' or 'd'. Read live so toggling debug at runtime
   * just works. */
  getDebugMode: () => boolean;
}

export function attachCatControls(deps: CatControlsDeps): void {
  const { canvas, world, focus, getCat, getDebugMode } = deps;

  // ── WASD + Shift ──────────────────────────────────────────────────
  // The cat's setKeyInput takes the full snapshot every time so we
  // just track held state here and re-publish on each change.
  // Backward (S) is treated as "stop" inside the cat — there's no
  // reverse-walk clip in the pack.
  const catKeys = {
    forward: false,
    backward: false,
    left: false,
    right: false,
    run: false,
  };
  const KEY_MAP: Record<string, keyof typeof catKeys> = {
    w: "forward",
    W: "forward",
    s: "backward",
    S: "backward",
    a: "left",
    A: "left",
    d: "right",
    D: "right",
    Shift: "run",
  };
  function publishCatKeys(): void {
    getCat()?.setKeyInput(catKeys);
  }
  // Clear and re-publish — used when leaving debug mode (or focusing
  // a text input) mid-keypress so a held-down key doesn't strand the
  // cat in a permanent walk because we'll never see its keyup.
  function resetCatKeys(): void {
    let any = false;
    for (const k of Object.keys(catKeys) as (keyof typeof catKeys)[]) {
      if (catKeys[k]) {
        catKeys[k] = false;
        any = true;
      }
    }
    if (any) publishCatKeys();
  }
  // Skip cat-key handling when the user is interacting with a real
  // input — typing "Berlin" into the search box shouldn't make the cat
  // walk left on each 'e'/'r'/'i'.
  function isTypingInForm(): boolean {
    const el = document.activeElement;
    return (
      el instanceof HTMLInputElement ||
      el instanceof HTMLTextAreaElement ||
      el instanceof HTMLSelectElement
    );
  }
  window.addEventListener("keydown", (e) => {
    const slot = KEY_MAP[e.key];
    if (!slot || focus.isActive() || e.repeat) return;
    if (!getDebugMode() || isTypingInForm()) return;
    if (catKeys[slot]) return;
    catKeys[slot] = true;
    publishCatKeys();
  });
  window.addEventListener("keyup", (e) => {
    const slot = KEY_MAP[e.key];
    if (!slot) return;
    if (!catKeys[slot]) return;
    catKeys[slot] = false;
    publishCatKeys();
  });
  // Releasing all keys when the user toggles off debug or tabs into a
  // form mid-press matters because keyup may then be eaten by the
  // input (or simply never arrive if focus moved). Without this the
  // cat would keep walking in the last direction forever.
  document.addEventListener("focusin", () => {
    if (isTypingInForm()) resetCatKeys();
  });

  // ── Right-click → cat waypoint ────────────────────────────────────
  // Raycast against the floor plane (y=0), not the cup meshes, so a
  // click on a cup still resolves to the floor point under it.
  // OrbitControls' right-mouse pan is disabled below to keep the
  // gesture exclusive.
  const waypointRaycaster = new THREE.Raycaster();
  const waypointPointer = new THREE.Vector2();
  const FLOOR_PLANE = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  const waypointHit = new THREE.Vector3();
  canvas.addEventListener("contextmenu", (e) => e.preventDefault());
  canvas.addEventListener("pointerdown", (e) => {
    if (e.button !== 2) return;
    if (focus.isActive()) return;
    const rect = canvas.getBoundingClientRect();
    waypointPointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    waypointPointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    waypointRaycaster.setFromCamera(waypointPointer, world.camera);
    if (waypointRaycaster.ray.intersectPlane(FLOOR_PLANE, waypointHit)) {
      getCat()?.setWaypoint(waypointHit);
    }
  });
  // OrbitControls pans on right-mouse by default. Override so
  // right-click is reserved for the cat waypoint.
  world.controls.mouseButtons.RIGHT = null;

  // ── Idle camera drift ─────────────────────────────────────────────
  // Slow azimuth wobble after a few idle seconds so the scene feels
  // alive when the user steps away. Pointer / wheel input resets the
  // idle timer.
  let idleT = 0;
  let lastInteract = performance.now();
  const wakeFromIdle = (): void => {
    lastInteract = performance.now();
    idleT = 0;
  };
  canvas.addEventListener("pointerdown", wakeFromIdle);
  canvas.addEventListener("wheel", wakeFromIdle, { passive: true });

  world.onUpdate((dt) => {
    if (performance.now() - lastInteract > 4000) {
      idleT += dt;
      const targetAzimuth = Math.sin(idleT * 0.1) * 0.05;
      world.controls.target.x = targetAzimuth;
    }
  });
}
